import { Component, input, output, ViewChild, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { QaiButtonComponent, QaiDialogComponent } from '@qai/ui';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { CertificationAcknowledgementService } from 'src/app/services/learner/certification-acknowledgement.service';

@Component({
  selector: 'app-certification-acknowledgement-modal',
  standalone: true,
  imports: [QaiButtonComponent, QaiDialogComponent, TranslocoModule],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'call' }],
  templateUrl: './certification-acknowledgement-modal.component.html',
  styleUrls: ['./certification-acknowledgement-modal.component.scss'],
})
export class CertificationAcknowledgementModalComponent implements AfterViewInit, OnInit, OnDestroy {
  moduleAssignmentUuid = input<string>('');
  isCameraInterruption = input<boolean>(false);
  acknowledged = output<string>();
  cancelled = output<void>();

  @ViewChild('dialog') dialogRef: QaiDialogComponent | undefined;

  isLoading = false;
  isCameraOn = false;
  private cameraCheckInterval: ReturnType<typeof setInterval> | undefined;
  private cameraStream: MediaStream | null = null;

  constructor(
    private certAckService: CertificationAcknowledgementService
  ) {}

  ngOnInit(): void {
    this.initCameraCheck();
  }

  ngAfterViewInit(): void {
    this.dialogRef?.showModal();
  }

  ngOnDestroy(): void {
    if (this.cameraCheckInterval) {
      clearInterval(this.cameraCheckInterval);
    }
    this.stopCameraStream();
  }

  private initCameraCheck(): void {
    this.acquireCameraStream();
    this.cameraCheckInterval = setInterval(() => {
      if (this.isCameraOn) {
        // Camera is on — just verify the existing track is still live
        const t = this.cameraStream?.getVideoTracks()[0];
        if (!t || t.readyState !== 'live') {
          this.isCameraOn = false;
          this.stopCameraStream();
        }
      } else {
        // Camera is off — try to re-acquire in case user re-enabled it
        this.acquireCameraStream();
      }
    }, 1000);
  }

  private acquireCameraStream(): void {
    navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      .then(stream => {
        this.stopCameraStream();
        this.cameraStream = stream;
        const track = stream.getVideoTracks()[0];
        this.isCameraOn = !!track && track.readyState === 'live';
        if (track) {
          track.onended = () => {
            this.isCameraOn = false;
            this.cameraStream = null;
          };
        }
      })
      .catch(() => {
        this.isCameraOn = false;
      });
  }

  private stopCameraStream(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(t => t.stop());
      this.cameraStream = null;
    }
  }

  async acceptAndBegin(): Promise<void> {
    if (!this.isCameraOn) return;

    if (this.isCameraInterruption()) {
      this.acknowledged.emit('');
      return;
    }

    this.isLoading = true;
    this.certAckService.acknowledge(this.moduleAssignmentUuid()).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.sessionToken) {
          this.acknowledged.emit(response.sessionToken);
        }
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onCancel(): void {
    this.cancelled.emit();
  }
}
